import angular from 'angular';
import SignalRService from '@/shared/services/SignalRService';
import DocumentService from '@/shared/services/DocumentService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import moment from 'moment';
import { generateGeneratedCodeExample } from '../common/directives/generatedCodeFormatDrv';

angular
  .module('cxSettings')
  .controller('GeneralSettingsCtrl', [
    '$scope',
    '$http',
    'flySvc',
    'optionsSvc',
    'dataCache',
    'templateHttpSvc',
    'resourceFactorySvc',
    'appUrls',
    '$translate',
    '$rootScope',
    '$timeout',
    '$window',
    'companyId',
    'resourceNameFilter',
    'dialogsSvc',
    'environment',
    '$filter',
    'ModalService',
    'translateListFilter',
    'settingsDialogsSvc',
    '$location',
    '$anchorScroll',
    'enumCacheSvc',
    'checkSecurity',
    generalSettingsCtrl,
  ]);

function generalSettingsCtrl(
  $scope,
  $http,
  flySvc,
  optionsSvc,
  dataCache,
  templateHttpSvc,
  resourceFactorySvc,
  appUrls,
  $translate,
  $rootScope,
  $timeout,
  $window,
  companyId,
  resourceNameFilter,
  dialogsSvc,
  environment,
  $filter,
  ModalService,
  translateListFilter,
  settingsDialogsSvc,
  $location,
  $anchorScroll,
  enumCacheSvc,
  checkSecurity,
) {
  $scope.exampleLanguage = $translate.use();
  $scope.templates = [];
  $scope.resources = [];
  $scope.connectWebUrl = `${appUrls.connect}`;
  $scope.companies = [];
  $scope.documentSettings = {};
  $scope.selectedCompany = {};
  $scope.ownCompanyRelations = [];
  $scope.employeeProfiles = [];
  $scope.absenceRelation = null;
  $scope.environment = (environment || '').toLowerCase();
  $scope.isProduction = $scope.environment === 'production';
  $scope.savingComplianceSettings = false;
  $scope.hideLedgerSetting = false;
  $scope.hasSso = false;
  $scope.minimumDateOptions = {
    minDate: new Date(1900, 0, 1),
    maxDate: new Date(),
    initDate: new Date(),
    startingDay: 1,
  };
  const hideSettingsIfNeeded = () => {
    let createdOn = dataCache.getArray('createdOn');

    if (createdOn) {
      var releaseDate = moment('08-11-2021', 'DD-MM-YYYY');
      if (moment.utc(createdOn).isAfter(releaseDate)) {
        $scope.hideLedgerSetting = true;
      }
    }
    $scope.hasSso = dataCache.getArray('ssoProvider') > 0;
  };

  hideSettingsIfNeeded();

  const retrieveCompanies = () => {
    dataCache.ready().then(function() {
      $scope.companies = dataCache.getArray('company');
      if (companyId) {
        $scope.selectedCompany = $scope.companies.find(x => x.id == companyId); // select company based on passed CompanyId
      } else {
        $scope.selectedCompany = $scope.companies[0];
      }

      $scope.companyChanged($scope.selectedCompany, true);
    });
  };
  retrieveCompanies();

  const setAbsenceRelation = relationId => {
    if (!relationId) {
      $scope.absenceRelation = null;
      return;
    }

    resourceFactorySvc.get('relation').ids([relationId], function(data) {
      if (data?.length > 0) {
        $scope.absenceRelation = data[0];
      } else {
        $scope.absenceRelation = null;
      }
    });
  };

  $scope.companyChanged = (company, initialLoad = false) => {
    if (!company) return;
    $scope.settings = null;
    $scope.loading = true;
    $scope.selectedCompany = $scope.companies.find(x => x.id == company.id);
    $scope.connectWebUrl = `${appUrls.connect}?companyId=${$scope.selectedCompany.id}`;

    if (checkSecurity('Settings:r')) {
      resourceFactorySvc
        .get('settings')
        .company({ id: company.id })
        .$promise.then(
          function(result) {
            $scope.settings = result;
            result.complianceSettings = result.complianceSettings
              .map(x => {
                return {
                  ...x,
                  label: $filter('translateList')(
                    x.complianceType,
                    'complianceType',
                    x.complianceType,
                  ),
                };
              })
              .sort(dynamicSort('label'));
            const half = Math.ceil((result.complianceSettings || []).length / 2);
            $scope.complianceSettingsCol1 = result.complianceSettings
              ? result.complianceSettings.slice(0, half)
              : [];
            $scope.complianceSettingsCol2 = result.complianceSettings
              ? result.complianceSettings.slice(half)
              : [];

            $scope.attachResources();

            if (initialLoad) {
              const hash = $location.hash();
              if (hash) {
                $timeout(() => {
                  $anchorScroll(hash);
                }, 100);
              }
            }
          },
          function(error) {
            flySvc.error(error.data.code);
            $scope.settings = {};
          },
        );
    }

    if (checkSecurity(['Settings:r', 'Users:u'])) {
      resourceFactorySvc
        .get('settings')
        .hrm({ id: company.id })
        .$promise.then(
          function(result) {
            $scope.hrmSettings = result;
            setAbsenceRelation(result.absenceRelationId);
          },
          function(error) {
            flySvc.error(error);
          },
        );
    }

    $scope.loading = false;
  };

  const companyUpdatedHandler = company => {
    if (!$scope.settings || $scope.settings.companyId != company.companyId) return;
    $timeout(function() {
      $scope.settings = { ...$scope.settings, ...company };
    }, 100);
  };
  SignalRService.subscribe('companyUpdated', companyUpdatedHandler);

  $scope.$on('$destroy', function() {
    SignalRService.unsubscribe('companyUpdated', companyUpdatedHandler);
  });

  if (checkSecurity('Settings:r')) {
    $http.get(appUrls.documents + '/api/settings/').then(({ data }) => {
      $scope.documentSettings = data;
    });

    templateHttpSvc.getTemplates().then(function(data) {
      // add ability to select "none" for payment registration template context
      $scope.templates = [
        ...data,
        {
          context: 8,
          name: $translate.instant('None'),
          id: null,
        },
        {
          context: 2,
          name: $translate.instant('None'),
          id: null,
        },
      ];
    });

    resourceFactorySvc.get('resource').reg(
      {
        type: 0,
      },
      function(data) {
        $scope.resources = data.map(x => ({
          ...x,
          code: x.code || '',
          name: resourceNameFilter(x),
        }));
        $scope.attachResources();
      },
    );
  }

  $scope.VATPercentages = optionsSvc.getOptions('vatPercentage');

  $scope.attachResources = function() {
    if (!$scope.settings || !$scope.resources) return;
    $scope.settings.defaultTaskResource = $scope.settings.defaultTaskResourceId
      ? $scope.resources.find(function(r) {
          return r.id === $scope.settings.defaultTaskResourceId;
        })
      : null;
  };

  $scope.openConnect = function() {
    $window.open($scope.connectWebUrl, '_blank');
  };

  $scope.openRecognitionOptions = function() {
    ModalService.showModal({
      template: require('./dialogs/recognitionOptionsDialog.html'),
      controller: 'RecognitionOptionsDialogCtrl',
      controllerAs: 'vm',
      locationChangeSuccess: false,
      inputs: {
        magicMailAllowedRecognitionSenders:
          $scope.documentSettings.magicMailAllowedRecognitionSenders,
        linkEmailToCrmOther: $scope.documentSettings.linkEmailToCrmOther,
        linkEmailToCrmProspect: $scope.documentSettings.linkEmailToCrmProspect,
        linkEmailToCrmOwnCompany: $scope.documentSettings.linkEmailToCrmOwnCompany,
      },
    }).then(function(modal) {
      modal.element.modal();
      modal.element.on('hidden.bs.modal', function(e) {
        modal.element.off(e); //Remove event
        modal.element.remove(); //Remove element
      });

      modal.close.then(function(result) {
        if (result === null) return; // cancelled
        $scope.submitAllDocSettings(result);
      });
    });
  };

  $scope.openCompanyHolidays = function() {
    if (!$scope.settings || !$scope.settings.companyId) return;

    ModalService.showModal({
      template: require('./dialogs/companyHolidaysDialog.html'),
      controller: 'CompanyHolidaysDialogCtrl',
      controllerAs: 'vm',
      locationChangeSuccess: false,
      inputs: {
        companyId: $scope.settings.companyId,
      },
    }).then(function(modal) {
      modal.element.modal();
      modal.element.on('hidden.bs.modal', function(e) {
        modal.element.off(e); //Remove event
        modal.element.remove(); //Remove element
      });
    });
  };

  $scope.openCreateEmailDomain = function() {
    if (!$scope.selectedCompany.email) {
      flySvc.error('msgCompanyEmailRequiredBeforeVerification');
      return;
    }

    ModalService.showModal({
      template: require('./dialogs/addEmailDomainDialog.html'),
      controller: 'AddEmailDomainDialogCtrl',
      controllerAs: 'vm',
      locationChangeSuccess: false,
      inputs: {
        suggestedEmailDomain: $scope.settings.suggestedEmailDomain,
        companyId: $scope.settings.companyId,
        companyEmail: $scope.selectedCompany.email,
      },
    }).then(function(modal) {
      modal.element.modal();
      modal.element.on('hidden.bs.modal', function(e) {
        modal.element.off(e); //Remove event
        modal.element.remove(); //Remove element
      });

      modal.close.then(function(result) {
        if (!result) return;
        const model = {
          ...$scope.settings,
          emailDomain: result,
        };

        angular.extend($scope.settings, model);
        flySvc.success('msgEmailDomainAddedVerificationPending', { domain: result.domain });
        $scope.openCnameInfoDialog();
      });
    });
  };

  $scope.openDeleteEmailDomain = function() {
    if (!$scope.settings || !$scope.settings.companyId) return;

    dialogsSvc.showYesNo('comfirmDeleteTitle', 'confirmDeleteEmailDomain').then(function() {
      $http
        .delete(appUrls.api + '/api/settings/DeleteEmailDomain?id=' + $scope.settings.companyId)
        .then(function(response) {
          const result = response.data;
          const model = {
            ...$scope.settings,
            emailUseCustomFrom: false,
            emailDomain: undefined,
          };

          angular.extend($scope.settings, model);
          flySvc.success();
        })
        .catch(function(error) {
          flySvc.error(error);
          return '';
        });
    });
  };

  $scope.verifyEmailDomain = function() {
    if (!$scope.settings || !$scope.settings.companyId) return;
    $scope.verifying = true;

    $http({
      method: 'get',
      url: appUrls.api + '/api/settings/VerifyEmailDomain?id=' + $scope.settings.companyId,
    })
      .then(function(response) {
        const result = response.data;
        const model = {
          ...$scope.settings,
          emailUseCustomFrom: result.status === 1,
          emailDomain: result,
        };

        angular.extend($scope.settings, model);
        if (result.status === 0) {
          // verification failed
          flySvc.error('msgEmailDomainVerificationFailed', {
            domain: result.domain,
          });
        } else if (result.status === 1) {
          // verification succeeded
          flySvc.success('msgEmailDomainVerificationSucceeded', {
            domain: result.domain,
          });
        }
      })
      .catch(function(error) {
        flySvc.error(error);
      })
      .finally(function() {
        $scope.verifying = false;
      });
  };

  $scope.openCnameInfoDialog = function() {
    if (!$scope.settings || !$scope.settings.emailDomain) return;
    ModalService.showModal({
      template: require('./dialogs/emailDomainCnameInfoDialog.html'),
      controller: 'EmailDomainCnameInfoDialogCtrl',
      controllerAs: 'vm',
      locationChangeSuccess: false,
      inputs: {
        emailDomain: $scope.settings.emailDomain,
        companyId: $scope.settings.companyId,
      },
    }).then(function(modal) {
      modal.element.modal();
      modal.element.on('hidden.bs.modal', function(e) {
        modal.element.off(e); //Remove event
        modal.element.remove(); //Remove element
      });

      modal.close.then(function(result) {
        if (!result) return;
        const model = {
          ...$scope.settings,
          emailUseCustomFrom: result.status === 1,
          emailDomain: result,
        };

        angular.extend($scope.settings, model);
      });
    });
  };

  var updateCompany = (model, showSuccess, customFromEnabled) => {
    return resourceFactorySvc
      .get('settings')
      .saveCompany(model)
      .$promise.then(
        function(result) {
          if (result) {
            $scope.settings = result;
            var dataCacheCompany = dataCache.getCompanySettings(result.companyId);
            if (dataCacheCompany) {
              const model = {
                ...result,
                sendInvoiceTemplateId: result.templateSendInvoice // settings object has entire template object iso only id
                  ? result.templateSendInvoice.id
                  : null,
              };
              angular.extend(dataCacheCompany, model);
            }
            //Propagate settings to all companies
            const {
              defaultTaskResourceId,
              taskTimeRegistrationMethod,
              sendInvoiceUseMailEditor,
              forceLinkingDocumentsToRelation,
              registrationMinimumDate,
              absenceMinimumDate,
            } = result;
            dataCache.updateAllCompanies({
              defaultTaskResourceId,
              taskTimeRegistrationMethod,
              sendInvoiceUseMailEditor,
              forceLinkingDocumentsToRelation,
              registrationMinimumDate,
              absenceMinimumDate,
            });
            $scope.attachResources();
          }
          if (customFromEnabled && (!result.emailDomain || result.emailDomain.status !== 1)) {
            flySvc.info('msgEmailDomainVerificationRequired', {
              companyEmail: $scope.selectedCompany.email,
            });
            return false;
          }
          if (showSuccess) {
            flySvc.success('msgChangesSavedForName', { name: $scope.selectedCompany.name });
          }
          return false;
        },
        error => flySvc.error(error),
      );
  };

  $scope.submitHrmSettings = function(value, field, showSuccess = true) {
    if ($scope.hrmSettings[field] === value) return false;
    if (typeof value === 'undefined' && field != 'absenceRelationId') return ''; // Validation error

    var model = angular.copy($scope.hrmSettings);
    model[field] = value;

    return resourceFactorySvc
      .get('settings')
      .saveHrm({ id: $scope.selectedCompany.id }, model)
      .$promise.then(
        function(result) {
          $scope.hrmSettings = model;
          flySvc.success('msgChangesSavedForName', { name: $scope.selectedCompany.name });
          return false;
        },
        error => flySvc.error(error),
      );
  };

  $scope.submit = function(value, field, showSuccess = true) {
    if ($scope.settings[field] === value) return false;
    if (typeof value === 'undefined' && field != 'absenceRelationId') return ''; // Validation error

    var model = angular.copy($scope.settings);
    model[field] = value;

    if (field === 'defaultTaskResource') {
      model.defaultTaskResourceId = value ? value.id : null;
      delete model.resource;
    }

    if (field === 'fixedAmountLedgerAccount' || field === 'advanceSettlementLedgerAccount') {
      return dialogsSvc
        .showYesNoCancel(
          'msgConfirmPropogateChangesTitle',
          'msgPropagateLedgerAccountChangesToExistingSchedule',
        )
        .then(
          function(result) {
            if (result === null) return false; // Cancel changes

            if (result) {
              model.propagateLedgerChanges = true;
            } else {
              model.propagateLedgerChanges = false;
            }

            return updateCompany(model, showSuccess);
          },
          function() {
            return false; // Cancel changes
          },
        );
    } else {
      var customFromEnabled = field === 'emailUseCustomFrom' && value;
      return updateCompany(model, showSuccess, customFromEnabled);
    }
  };

  $scope.submitAllDocSettings = function(settings) {
    var model = angular.copy($scope.documentSettings);
    model = {
      ...model,
      ...settings,
    };

    return $http.put(appUrls.documents + '/api/settings/', model).then(
      ({ data }) => {
        if (data) {
          $scope.documentSettings = data;
        }
        flySvc.success('msgConfirmChange');
        return false;
      },
      ({ data: { code, parameters } = {} }) => {
        flySvc.error(code, parameters, null, 'documentdomainexceptioncode');
        return '';
      },
    );
  };

  $scope.submitDocSettings = function(value, field, showSuccess = true) {
    if ($scope.documentSettings[field] === value) return false;
    if (typeof value === 'undefined') return ''; // Validation error

    var model = angular.copy($scope.documentSettings);
    model[field] = value;

    return $http.put(appUrls.documents + '/api/settings/', model).then(
      ({ data }) => {
        if (data) {
          $scope.documentSettings = data;
        }
        if (showSuccess) flySvc.success('msgConfirmChange');
        return false;
      },
      ({ data: { code, parameters } = {} }) => {
        flySvc.error(code, parameters, null, 'documentdomainexceptioncode');
        return '';
      },
    );
  };
  $scope.translateInvoiceText = function() {
    if (!$scope.settings || !$scope.settings.companyId) return;
    dialogsSvc
      .translateListItem(
        'companyinvoicetext',
        $scope.settings.companyId,
        $scope.settings.invoiceText,
        {
          inputRows: 7,
          defaultRequired: false,
          maxlength: 500,
        },
      )
      .then(function(result) {
        if (result) {
          //This is a special case for the translations.
          //If we use the default as fallback, you can't leave another language empty.
          //It will use the fallback.
          $scope.submit(null, 'invoiceText');
        }
      });
  };

  $scope.translateAdminCostText = function() {
    if (!$scope.settings || !$scope.settings.companyId) return;
    dialogsSvc.translateListItem('companyadmincosttext', $scope.settings.companyId, null, {
      inputRows: 1,
      defaultRequired: false,
      maxlength: 500,
      placeholders: {
        //Hardcoded, back-end uses invoicelinetype 8 translations
        nl: 'Administratieve kost',
        fr: 'Coûts administratifs',
        en: 'Administrative cost',
      },
    });
  };

  $scope.translateEmailRemark = function() {
    if (!$scope.settings || !$scope.settings.companyId) return;
    dialogsSvc
      .translateListItem(
        'companyemailremark',
        $scope.settings.companyId,
        $scope.settings.emailRemark,
        {
          inputRows: 7,
          defaultRequired: false,
          maxlength: 500,
        },
      )
      .then(function(result) {
        if (result) {
          //This is a special case for the translations.
          //If we use the default as fallback, you can't leave another language empty.
          //It will use the fallback.
          $scope.submit(null, 'emailRemark');
        }
      });
  };

  $scope.updateComplianceSetting = function(setting) {
    if (!setting) return;
    $scope.savingComplianceSettings = true;

    return $http.put(appUrls.api + '/api/settings/UpdateComplianceSettings', setting).then(
      () => {
        $scope.savingComplianceSettings = false;
        if (setting.shouldTrack) {
          flySvc.success('msgComplianceRecalculationStarted');
        } else {
          flySvc.success('msgConfirmChange');
        }
        return true;
      },
      error => {
        $scope.savingComplianceSettings = false;
        flySvc.error(error);
        return '';
      },
    );
  };

  enumCacheSvc.get('employeeprofile').then(function(data) {
    $scope.employeeProfiles = data.sort(dynamicSort('key'));
  });

  $scope.getDefaultTranslationForEmployeeProfile = function(profile) {
    switch (profile) {
      case 1:
        return $translate.instant('Responsible');
      case 2:
        return $translate.instant('Assistant');
      case 3:
        return $translate.instant('AssistantBackup');
      case 4:
        return $translate.instant('Associate');
      case 5:
        return $translate.instant('BillingUser');
      case 6:
        return $translate.instant('SeniorUser');
      default:
        return null;
    }
  };

  $scope.translateEmployeeProfile = function(profile) {
    dialogsSvc.translateListItem(
      'employeeprofile',
      profile,
      $scope.getDefaultTranslationForEmployeeProfile(profile),
      {
        inputRows: 1,
        defaultRequired: false,
        maxlength: 250,
      },
    );
  };

  $scope.absenceRelationChanged = relation => {
    $scope.absenceRelation = relation;
    return $scope.submitHrmSettings(relation?.id, 'absenceRelationId');
  };

  $scope.editEmailSignatures = function() {
    if (!$scope.settings || !$scope.settings.companyId) return;
    settingsDialogsSvc.openEmailSignaturesDialog($scope.settings.companyId);
  };

  // for the "general setting" of the "general" reply-to type, we want to display a custom text without changing the enum
  $scope.displayReplyToType = function(value, customValueForZero) {
    if (value === 0 && customValueForZero) {
      return $translate.instant('E-mail of the own company');
    }
    // If the Type is one of the employee profiles, we need to translate it using the employee profile enum, which has the same order but is 2 lower
    if (value > 2) {
      return translateListFilter(value - 2, 'employeeprofile', value - 2);
    }
    return translateListFilter(value, 'emailReplyToType', value);
  };

  // for the "general setting" of the "general" sender name type, we want to display a custom text without changing the enum
  $scope.displaySenderNameType = function(value) {
    if (value === 0) return $translate.instant('Name of the own company');
    if (value > 2) {
      return translateListFilter(value - 2, 'employeeprofile', value - 2);
    }
    return translateListFilter(value, 'emailSenderNameType', value);
  };

  $scope.generateExample = function(format, startValue) {
    return format.replace('[Counter:Nx]', startValue);
  };

  $scope.generateCodeExample = function(format, startValue) {
    return generateGeneratedCodeExample(format, startValue);
  };
}
